// assets/js/utils/event-category.js
/**
 * Event kind of a form — the SINGLE owner of "what is this event?" for the
 * executive metrics page, the win-back job, the plan window's Signature line
 * and the builder's website banner. Dual-environment: the browser gets
 * window.EventCategory, the server requires this same file (via
 * server/utils/event-category.js).
 *
 * Precedence (2026-11-03):
 *   1. explicit — `occasion` set from a guide link (?event=), or a corporate
 *      form (eventType 'corporate' / planType 'corporate' / organizationId).
 *      The stored eventType is otherwise useless: every form carries 'event'.
 *   2. AI — `occasionAI`, written by server/jobs/occasion-classify.jobs.js
 *      (Haiku reads title, subtitle and opening line), when its confidence is
 *      at least AI_MIN_CONFIDENCE. Tested on 205 "Other" titles: 135 labelled
 *      confidently, the rest were placeholders nobody could read ("test",
 *      "event name", "q").
 *   3. rules — the title regexes below, the fallback for a brand-new form the
 *      job has not reached and for a failed call.
 *
 * Rules measured 2026-10-20 against agent labels of 2,098 titles: weddings 91%
 * recall, 98% precision; mitzvahs 84% / 100%; corporate only ~33% recall
 * (titles are mostly company names), 86% precision. Rule order matters (e.g.
 * "15 años" must hit Quinceañera before the generic \d+ años birthday rule;
 * "anniversaire de mariage" must hit Anniversary before the French-birthday
 * "anniversaire" rule).
 */
(function () {
  // The labels the AI may return (server/services/occasion-classifier.service.js)
  // and a guide link may set (?event=). Fine-grained on purpose; analysis
  // reads them through CATEGORY_OF.
  const OCCASIONS = [
    'wedding', 'engagement', 'henna', 'katb_kitab', 'birthday', 'bar_bat_mitzvah',
    'quinceanera', 'baby', 'baptism_communion', 'graduation', 'anniversary',
    'holiday_party', 'party', 'corporate', 'community', 'religious', 'memorial', 'other',
  ];
  const CATEGORY_OF = {
    wedding: 'Wedding & Engagement', engagement: 'Wedding & Engagement',
    henna: 'Wedding & Engagement', katb_kitab: 'Wedding & Engagement',
    birthday: 'Birthday', bar_bat_mitzvah: 'Bar/Bat Mitzvah',
    quinceanera: 'Quinceañera & Sweet 16', baby: 'Baby & Gender Reveal',
    baptism_communion: 'Baptism & Communion', graduation: 'Graduation',
    anniversary: 'Anniversary', holiday_party: 'Holiday & Seasonal', party: 'Party',
    corporate: 'Corporate & Community', community: 'Corporate & Community',
    religious: 'Other', memorial: 'Other', other: 'Other',
  };
  const AI_MIN_CONFIDENCE = 0.6;

  const isCorporate = (f) => String(f.eventType || '').toLowerCase() === 'corporate'
    || f.planType === 'corporate' || !!f.organizationId;

  // The occasion a form has been given, or null when only the title rules can
  // say. { value, source: 'explicit' | 'ai' }.
  function occasionOf(f) {
    if (!f) return null;
    if (OCCASIONS.includes(f.occasion)) return { value: f.occasion, source: 'explicit' };
    if (isCorporate(f)) return { value: 'corporate', source: 'explicit' };
    if (String(f.eventType || '').toLowerCase() === 'wedding') return { value: 'wedding', source: 'explicit' };
    const ai = f.occasionAI;
    if (ai && OCCASIONS.includes(ai.value) && Number(ai.confidence) >= AI_MIN_CONFIDENCE) {
      return { value: ai.value, source: 'ai' };
    }
    return null;
  }

  function categorizeByTitle(f) {
    const t = String(f.title || '').toLowerCase();
    if (!t.trim()) return 'Other';
    const RULES = [
      ['Bar/Bat Mitzvah', /bar.?\s?mit[sz]|bat.?\s?mit[sz]|בר מצו|בת מצו|בר-מצו|בת-מצו|בר המצו|בת המצו|עליה לתורה/],
      ['Quinceañera & Sweet 16', /quincea|quince años|sweet\s*1[56]|\b15 años|\b15 anos|\bxv años|^xv\b|\bxv de\b|\bmis xv\b|debut\b|\bquince\b/],
      ['Baby & Gender Reveal', /baby\s?shower|babyparty|baby[- ]?sprinkle|gender reveal|about to pop|chá de bebê|cha de bebe|chá revelação|ברית|בריתה|baby naming|babyborrel|chá de fralda|chá do |chá da |cha do |cha da /],
      ['Baptism & Communion', /baptism|bapt[êe]me|bautizo|batizado|christening|communion|comuni[óo]n|comunhão|konfirmation|confirmaci[óo]n|crisma|taufe|עלייה לתורה/],
      ['Anniversary', /anniversaire de mariage|aniversario de (matrimonio|bodas)|wedding anniversary|\banniversary\b|\baniversario\b|jubil[äa]um|jubileu|jaar getrouwd|años de casados|years of/],
      // Second line: misses found by an agent reading all 2,098 titles
      // since Apr 2026 (2026-10-20) — Arabic weddings, "X weds Y", civil /
      // registered ceremonies, pre-wedding events, common typos.
      ['Wedding & Engagement', /weddi|weddin|wending|\bweds\b|vow renewal|elopement|trauung|heiraten|casamos|newlyweds|getting married|tied the knot|walima|urus\b|solemni[sz]ation|registered partnership|chuppah|מתחתנים|vjenčanje|kāz|sangeet|mehndi|haldi|bachelor|despedida de solter|rehearsal dinner|save the date|reservá la fecha|bridal|engagment|fian[çc]aille|ruracio|roora|زفاف|زواج|عقد قران|عرس|خطوب|خطبة|حنة|حنه|hochzeit|\bboda\b|casamento|mariage|חתונה|düğün|dugun|nikah|nikkah|shadi|alambamento|\bkasal\b|engagement|verlobung|fiançailles|noivado|חינה|אירוסין|חופה|bridal shower|nuptial|matrimonio|trouwfeest|bruiloft|vestuv|esküvő|svadba|svatba|ślub|γάμος|свадьб|весілл|\bhenna\b|حناء|حنّاء/],
      ['Birthday', /birthday|b-?day|geburtstag|cumple|urodziny|יום הולדת|anivers[áa]rio|anniversaire|verjaardag|compleanno|doğum günü|aninho|עיגול|\b\d+\s*(ans?|anos?|años?|jahre?|jaar|år)\b|turns \d+|sweet \d+|@ ?\d+\b|^\d+\s+(da|do|de)\s+\w|день рождения|יומולדת|عيد ميلاد|誕生日/],
      ['Graduation', /graduat|grad party|grad celebration|commencement|abschluss|formatura|graduaci[óo]n|promoci[óo]n de|סיום לימודים|laurea|تخرج/],
      // Holidays and seasons (2026-11-03): 37 of 205 'Other' titles that
      // autumn were Halloween, Christmas, Diwali or Thanksgiving parties.
      // After Birthday, so 'عيد ميلاد' (birthday) never reads as Eid.
      ['Holiday & Seasonal', /halloween|christmas|x-?mas|navidad|\bnatal\b|noël|\bnoel\b|weihnacht|thanksgiving|friendsgiving|diwali|deepavali|hanuk|chanuk|חנוכה|purim|פורים|rosh hashan|ראש השנה|passover|\bseder\b|פסח|\beid\b|iftar|ramadan|عيد|إفطار|رمضان|new year|\bnye\b|año nuevo|réveillon|silvester|holiday party|holiday reception|holiday gathering|holiday dinner|easter|pascua|páscoa|ostern|lohri|\bholi\b|navratri|garba|dussehra|ganpati|onam|pongal|kwanzaa|st\.? patrick|fourth of july|4th of july|día de muertos|posada/],
      ['Corporate & Community', /meeting|conference|banquet|gala\b|awards?\b|launch|summit|\bboard\b|annual|reunion|fundraiser|charity|networking|expo\b|seminar|workshop|kick-?off|trade mission|service award|company|\bfirma\b|כנס|ערב הוקרה|team|offsite|town ?hall|opening|inaugura|eröffnung|happy hour|retreat|roundtable|press|conf[ée]rence|executive|pop.?up|open day|staff|sales|partners?\b|premiere|presentaci[óo]n|personalfeier|השקת|افتتاح|رونمایی/]
    ];
    for (const [cat, re] of RULES) {
      if (re.test(t)) return cat;
    }
    // Pure couple-name titles ("Alice & Cyrus", "Alejo + Gabbie",
    // "Andrés y Gemi", "Angela ❤️ Samuel") are de-facto weddings in this
    // dataset: two short name parts joined by a couple connector, no digits.
    const couple = /^[^\d&+]{2,30}(\s(&|\+|y|e|et|und|ve)\s|\s?(❤️|♥|💍|🤍|❤|💕)\s?)[^\d&+]{2,30}$/iu;
    if (couple.test(f.title || '') && String(f.title).trim().split(/\s+/).length <= 6) {
      return 'Wedding & Engagement';
    }
    // Last, so a named occasion above always wins ("Maya's birthday party").
    if (/\bparty\b|\bfiesta\b|\bfesta\b|\bfête\b|soirée|get.?together|\bbbq\b|barbecue|potluck|מסיבה|حفلة/.test(t)) return 'Party';
    return 'Other';
  }

  const categorizeForm = (f) => {
    const o = occasionOf(f);
    return o ? (CATEGORY_OF[o.value] || 'Other') : categorizeByTitle(f || {});
  };

  // Where categorizeForm's answer came from, for the coverage line on the
  // admin metrics page.
  const categorySource = (f) => {
    const o = occasionOf(f);
    // An AI 'other' ("test", "Event RSVP") is as unknown as no label.
    if (o) return o.value === 'other' ? 'none' : o.source;
    return categorizeByTitle(f || {}) === 'Other' ? 'none' : 'rules';
  };

  // Which website a Signature host is offered: 'wedding' | 'party' | 'none'
  // (corporate gets no site pitch), or null when the event is unknown — the
  // callers then keep today's wedding wording.
  const PARTY_CATEGORIES = new Set(['Birthday', 'Bar/Bat Mitzvah', 'Quinceañera & Sweet 16',
    'Baby & Gender Reveal', 'Holiday & Seasonal', 'Party', 'Graduation']);
  function siteKindOf(f) {
    if (!f) return null;
    const cat = categorizeForm(f);
    if (cat === 'Wedding & Engagement' || cat === 'Anniversary') return 'wedding';
    if (cat === 'Corporate & Community') return 'none';
    if (PARTY_CATEGORIES.has(cat)) return 'party';
    return null;
  }

  const EventCategory = {
    OCCASIONS, CATEGORY_OF, AI_MIN_CONFIDENCE,
    occasionOf, categorizeForm, categorySource, siteKindOf,
  };
  if (typeof window !== 'undefined') window.EventCategory = EventCategory;
  if (typeof module !== 'undefined' && module.exports) module.exports = EventCategory;
})();
